import { Component, Input, OnInit, ViewEncapsulation } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";

import { select, Store } from "@ngrx/store";
import { Observable, Subscription } from "rxjs";
import { take } from "rxjs/operators";

import {
  PropertyDTO,
  DataTypeCode,
} from "src/app/models/Properties/propertyDTO";
import { AppState, loadedTenantState } from "src/app/store/reducers";
import { RolePermissionsService } from "../../services/role-permissions/role-permissions.service";

@Component({
  selector: "app-properties",
  templateUrl: "./properties.component.html",
  styleUrls: ["./properties.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class PropertiesComponent implements OnInit {
  _properties: PropertyDTO[] = [];
  _formArray: FormArray;
  _selectedProperties: PropertyDTO[] = [];

  dataTypeCode = DataTypeCode;

  tenantDataSub: Subscription;
  tenantData$: Observable<any>;
  dateTimeFormat: string;

  // Permissions
  public canWrite$: Observable<boolean>;

  @Input() title: string = "Properties.Properties";
  @Input()
  set properties(propertiesList: PropertyDTO[]) {
    this._properties = this.sortByMandatory(propertiesList ?? []);
    this.tryBuildForm();
  }
  
  @Input()
  set permissionResource(resource: string) {
    this.canWrite$ = this.rolePermissionsService.hasPermission(resource, "WRITE");
    this.tryBuildForm();
  }
  
  @Input()
  set formArray(value: FormArray) {
    this._formArray = value;
    this.tryBuildForm();
  }

  @Input()
  set selectedProperties(properties: PropertyDTO[]) {
    this._selectedProperties = properties ?? [];
    this.tryBuildForm();
  }

  constructor(private store: Store<AppState>, private fb: FormBuilder,
    public rolePermissionsService: RolePermissionsService
  ) {
    this.tenantData$ = this.store.pipe(select(loadedTenantState));
  }

  ngOnInit(): void {
    this.loadTenantDateTimeFomat();
  }

  private tryBuildForm() {
    if (
      !this._properties 
      || !this._formArray 
      || !this.canWrite$
    ) {
      return;
    }

    this.setPropertiesForm(this._properties);
  }

  loadTenantDateTimeFomat(): void {
    this.tenantDataSub = this.tenantData$.subscribe((data: any) => {
      if (data) {
        this.dateTimeFormat = data.datetimeFormat;
      }
    });
  }

  sortByMandatory(properties: PropertyDTO[]): PropertyDTO[] {
    return properties.slice().sort((x, y) => {
      return x?.mandatory === y?.mandatory ? 0 : x?.mandatory ? -1 : 1;
    });
  }

setPropertiesForm(properties: PropertyDTO[]): void {
  this.canWrite$
    .pipe(take(1))
    .subscribe((canEdit) => {
      this._formArray.clear();
      properties.forEach((property) => {
        const selectedProperty = this.getSelectedProperty(property, this._selectedProperties);
        const control = this.fb.control(
          selectedProperty ? selectedProperty.value : "",
          property.mandatory ? Validators.required : []
        );

        if (!canEdit) {
          control.disable();
        }

        this._formArray.push(this.fb.group({
          [property.code]: control
        }));
      });
    });
}

  getSelectedProperty(
    property: PropertyDTO,
    selectedProperties: PropertyDTO[]
  ): PropertyDTO {
    return selectedProperties.find((selProp) => selProp?.code === property?.code);
  }

  getFormLabel(propertyControl: FormGroup): string {
    const [propertyCode] = Object.keys(propertyControl?.controls);
    const property = this.getProperty(this._properties, propertyCode);
    return property.description;
  }

  getRequiredIndication(categoryControl: FormGroup): boolean {
    const [propertyCode] = Object.keys(categoryControl?.controls);
    const proeprty = this.getProperty(this._properties, propertyCode);
    return proeprty.mandatory;
  }

  getPropertyDataType(propertyControl: FormGroup): DataTypeCode {
    const [propertyCode] = Object.keys(propertyControl?.controls);
    const property = this.getProperty(this._properties, propertyCode);
    return property.dataTypeCode;
  }

  getProperty(properties: PropertyDTO[], propertyCode: string): PropertyDTO {
    return properties.find((property) => property?.code === propertyCode);
  }

  getFormControlName(propertyControl: FormGroup): string {
    const [propertyCode] = Object.keys(propertyControl?.controls);
    return propertyCode;
  }

  clearFormControl(propertyControl: FormGroup): void {
    propertyControl.controls[this.getFormControlName(propertyControl)].setValue("");
    this._formArray.markAsDirty();
  }
}
